import Modal from "components/common/Modal";
import Stack from "components/common/Stack";
import Text from "components/common/Text";
import JarvisButton from "components/JarvisButton";
import SectionTitle from "components/SectionTitle";
import React, { useState, useEffect } from "react";
import { useDispatch } from "react-redux";
import { useHistory } from "react-router-dom";
import { commonActions } from "stores/common/commonActions";
import { jarvsiRMSOnboardingActions } from "stores/jarvisRMS/jarvisRMSOnboarding/jarvisRMSOnboardingAction";
import { jarvisRMSOnboardingApis } from "stores/jarvisRMS/jarvisRMSOnboarding/jarvisRMSOnboardingApis";
import _get from "lodash.get";

function RMSDragNDrop({ data, portfolioId, processType }) {
  const [allStocks, setAllStocks] = useState([]);
  const [searchTerm, setSearchTerm] = useState("");
  const [dragItem, setDragItem] = useState(null);
  const [showConfirmationModal, setShowConfirmationModal] = useState(false);
  const [isMobileView, setIsMobileView] = useState(false);
  const dispatch = useDispatch();
  const history = useHistory();

  // Check for mobile view on component mount and window resize
  useEffect(() => {
    const checkMobileView = () => {
      setIsMobileView(window.innerWidth <= 768);
    };

    checkMobileView();
    window.addEventListener("resize", checkMobileView);

    return () => {
      window.removeEventListener("resize", checkMobileView);
    };
  }, []);

  useEffect(() => {
    if (data && data.length > 0) {
      const initializedStocks = data.map((stock) => ({
        ...stock,
        category: stock.stockTerm || "unassigned",
        id: stock.portfolioStockId || stock.stockId,
      }));
      setAllStocks(initializedStocks);
    }
  }, [data]);

  const handleDragStart = (e, stock) => {
    setDragItem(stock);
    e.dataTransfer.setData("text/plain", stock.portfolioStockId);
    e.dataTransfer.effectAllowed = "move";
  };

  const handleDragOver = (e, targetCategory) => {
    e.preventDefault();
    e.dataTransfer.dropEffect = "move";
  };

  const handleDrop = (e, targetCategory) => {
    e.preventDefault();
    if (dragItem && dragItem.category !== targetCategory) {
      setAllStocks((prevStocks) =>
        prevStocks.map((stock) =>
          stock.portfolioStockId === dragItem.portfolioStockId
            ? { ...stock, category: targetCategory }
            : stock
        )
      );
    }
    setDragItem(null);
  };

  // Mobile tap handler
  const handleStockTap = (stock, targetCategory) => {
    if (stock.category !== targetCategory) {
      setAllStocks((prevStocks) =>
        prevStocks.map((s) =>
          s.portfolioStockId === stock.portfolioStockId
            ? { ...s, category: targetCategory }
            : s
        )
      );
    }
  };

  const applyToAll = (category) => {
    setAllStocks((prevStocks) =>
      prevStocks.map((stock) => ({ ...stock, category }))
    );
  };

  const resetAll = () => {
    setAllStocks((prevStocks) =>
      prevStocks.map((stock) => ({ ...stock, category: "unassigned" }))
    );
  };

  const filteredStocks = allStocks.filter(
    (stock) =>
      stock.stockName.toLowerCase().includes(searchTerm.toLowerCase()) ||
      stock.stockSymbol.toLowerCase().includes(searchTerm.toLowerCase())
  );

  const unassignedStocks = allStocks.filter(
    (stock) => !stock.category || stock.category === "unassigned"
  );
  const longTermStocks = allStocks.filter(
    (stock) => stock.category === "longTerm"
  );
  const shortTermStocks = allStocks.filter(
    (stock) => stock.category === "shortTerm"
  );

  const handleReviewAgain = () => {
    setShowConfirmationModal(false);
  };

  const handleConfirm = () => {
    const requestData = {
      portfolioId: portfolioId,
      shortTermStock: shortTermStocks.map((stock) => ({
        stockSymbol: stock.stockSymbol,
      })),
      longTermStock: longTermStocks.map((stock) => ({
        stockSymbol: stock.stockSymbol,
      })),
    };

    jarvisRMSOnboardingApis
      .confirmPortfolioStock(requestData)
      .then(async (response) => {
        setShowConfirmationModal(false);
        dispatch(
          commonActions.setToast({
            message: "Stock preferences saved successfully!",
            type: "success",
          })
        );

        const confirmedPortfolioId = _get(response, "result.portfolioId", null);
        console.log("Confirmed portfolio ID:", confirmedPortfolioId);

        if (processType === "EXISTING_HOLDING") {
          if (confirmedPortfolioId) {
            dispatch(jarvsiRMSOnboardingActions.getOnboardingData());
            window.location.href = `/jarvis-rms/existing-holding/${confirmedPortfolioId}`;
            return;
          }
          alert(
            "Portfolio is not confirmed yet! Please contact support for help!"
          );
          return;
        }

        const dataObject = {
          productCode: "RMS",
        };
        await jarvisRMSOnboardingApis.rmsStageUpdate(dataObject);
        history.push("/jarvis-rms/onboarding/ckyc");
      })
      .catch((error) => {
        console.error("Error:", error);
        console.error("Error response:", error.response);

        dispatch(
          commonActions.setToast({
            message:
              error.response?.data?.message ||
              "Failed to save stock preferences. Please try again.",
            type: "error",
          })
        );
      });
  };

  const StockCard = ({ stock, showCategory = false, isMobile = false }) => (
    <div
      draggable={!isMobileView}
      onDragStart={!isMobileView ? (e) => handleDragStart(e, stock) : undefined}
      onClick={isMobileView ? () => {} : undefined}
      className="stock-card"
      style={{
        padding: "12px",
        margin: "8px 0",
        border: `1px solid ${
          stock.category === "unassigned" ? "#e0e0e0" : "#10b981"
        }`,
        borderRadius: "8px",
        backgroundColor:
          stock.category === "unassigned" ? "#f9f9f9" : "#E5F7D04D",
        cursor: isMobileView ? "pointer" : "grab",
        transition: "all 0.2s ease",
        userSelect: "none",
        position: "relative",
      }}
    >
      <div style={{ fontWeight: "bold", fontSize: "14px" }}>
        {stock.stockName}
      </div>
      <div style={{ color: "#666", fontSize: "12px" }}>{stock.stockSymbol}</div>

      {/* Mobile action buttons */}
      {isMobileView && stock.category === "unassigned" && (
        <div style={{ display: "flex", gap: "8px", marginTop: "8px" }}>
          <button
            onClick={(e) => {
              e.stopPropagation();
              handleStockTap(stock, "longTerm");
            }}
            style={{
              padding: "10px 8px",
              fontSize: "10px",
              backgroundColor: "#10b981",
              color: "white",
              border: "none",
              borderRadius: "4px",
              cursor: "pointer",
              flex: 1,
            }}
          >
            Long Term
          </button>
          <button
            onClick={(e) => {
              e.stopPropagation();
              handleStockTap(stock, "shortTerm");
            }}
            style={{
              padding: "10px 8px",
              fontSize: "10px",
              backgroundColor: "#f59e0b",
              color: "white",
              border: "none",
              borderRadius: "4px",
              cursor: "pointer",
              flex: 1,
            }}
          >
            Short Term
          </button>
        </div>
      )}

      {/* Category badge */}
      {showCategory && stock.category !== "unassigned" && (
        <div
          style={{
            position: "absolute",
            top: "8px",
            right: "8px",
            fontSize: "10px",
            padding: "2px 6px",
            borderRadius: "4px",
            backgroundColor:
              stock.category === "longTerm" ? "#10b981" : "#f59e0b",
            color: "white",
            fontWeight: "bold",
          }}
        >
          {stock.category === "longTerm" ? "LONG" : "SHORT"}
        </div>
      )}
    </div>
  );

  const showModalHandler = () => {
    if (unassignedStocks.length > 0) {
      dispatch(
        commonActions.setToast({
          message: `Please assign all ${unassignedStocks.length} stock(s) to either Long Term or Short Term holding preference before continuing.`,
        })
      );
    } else {
      setShowConfirmationModal(true);
    }
  };

  // Mobile-specific layout
  if (isMobileView) {
    return (
      <>
        <SectionTitle
          sx={{ mb: 3 }}
          heading="Set Your Stock Holding Preferences"
          subheading="Tap on the action buttons to assign each stock to Long Term or Short Term holding preference."
        />

        {/* Top Control Row */}
        <div
          style={{
            display: "flex",
            flexDirection: "column",
            gap: "16px",
            marginBottom: "24px",
            padding: "16px",
            backgroundColor: "#f8fafc",
            borderRadius: "8px",
            border: "1px solid #e2e8f0",
          }}
        >
          {/* Quick Apply to All Section */}
          <div>
            <span
              style={{
                fontWeight: "600",
                color: "#374151",
                fontSize: "14px",
                display: "block",
                marginBottom: "8px",
              }}
            >
              Quick Apply to All:
            </span>
            <div style={{ display: "flex", gap: "8px", flexWrap: "wrap" }}>
              <button
                onClick={() => applyToAll("longTerm")}
                style={{
                  padding: "8px 12px",
                  color: "#2B70C5",
                  border: "2px solid #EEF4FA",
                  borderRadius: "6px",
                  fontSize: "12px",
                  cursor: "pointer",
                  fontWeight: "500",
                  flex: 1,
                  minWidth: "100px",
                }}
              >
                Long Term
              </button>
              <button
                onClick={() => applyToAll("shortTerm")}
                style={{
                  padding: "8px 12px",
                  color: "#2B70C5",
                  border: "2px solid #EEF4FA",
                  borderRadius: "6px",
                  fontSize: "12px",
                  cursor: "pointer",
                  fontWeight: "500",
                  flex: 1,
                  minWidth: "100px",
                }}
              >
                Short Term
              </button>
              <button
                onClick={resetAll}
                style={{
                  padding: "8px 12px",
                  backgroundColor: "#ef4444",
                  color: "white",
                  border: "none",
                  borderRadius: "6px",
                  fontSize: "12px",
                  cursor: "pointer",
                  fontWeight: "500",
                  flex: 1,
                  minWidth: "100px",
                }}
              >
                Reset All
              </button>
            </div>
          </div>

          {/* Search Stock Section */}
          <div style={{ position: "relative" }}>
            <input
              type="text"
              placeholder="Search stocks by name or symbol..."
              value={searchTerm}
              onChange={(e) => setSearchTerm(e.target.value)}
              style={{
                width: "100%",
                padding: "10px 16px 10px 40px",
                border: "1px solid #d1d5db",
                borderRadius: "6px",
                fontSize: "14px",
                outline: "none",
              }}
            />
            <svg
              style={{
                position: "absolute",
                left: "12px",
                top: "50%",
                transform: "translateY(-50%)",
                width: "16px",
                height: "16px",
                color: "#9ca3af",
              }}
              fill="none"
              stroke="currentColor"
              viewBox="0 0 24 24"
            >
              <path
                strokeLinecap="round"
                strokeLinejoin="round"
                strokeWidth={2}
                d="M21 21l-6-6m2-5a7 7 0 11-14 0 7 7 0 0114 0z"
              />
            </svg>
          </div>
        </div>

        {/* Summary Cards */}
        <div style={{ display: "flex", gap: "12px", marginBottom: "20px" }}>
          <div
            style={{
              flex: 1,
              padding: "12px",
              backgroundColor: "#f0fdf4",
              border: "1px solid #10b981",
              borderRadius: "8px",
              textAlign: "center",
            }}
          >
            <div style={{ fontSize: "12px", color: "#374151" }}>Long Term</div>
            <div
              style={{ fontSize: "16px", fontWeight: "bold", color: "#10b981" }}
            >
              {longTermStocks.length}
            </div>
          </div>
          <div
            style={{
              flex: 1,
              padding: "12px",
              backgroundColor: "#fffbeb",
              border: "1px solid #f59e0b",
              borderRadius: "8px",
              textAlign: "center",
            }}
          >
            <div style={{ fontSize: "12px", color: "#374151" }}>Short Term</div>
            <div
              style={{ fontSize: "16px", fontWeight: "bold", color: "#f59e0b" }}
            >
              {shortTermStocks.length}
            </div>
          </div>
          <div
            style={{
              flex: 1,
              padding: "12px",
              backgroundColor: "#f9fafb",
              border: "1px solid #d1d5db",
              borderRadius: "8px",
              textAlign: "center",
            }}
          >
            <div style={{ fontSize: "12px", color: "#374151" }}>Unassigned</div>
            <div
              style={{ fontSize: "16px", fontWeight: "bold", color: "#6b7280" }}
            >
              {unassignedStocks.length}
            </div>
          </div>
        </div>

        {/* Stocks List */}
        <div
          style={{
            padding: "16px",
            border: "1px solid #e5e7eb",
            borderRadius: "8px",
            backgroundColor: "white",
          }}
        >
          <h3
            style={{
              margin: "0 0 16px 0",
              color: "#374151",
              fontSize: "16px",
              fontWeight: "600",
            }}
          >
            All Stocks ({allStocks.length})
          </h3>
          <div
            style={{
              maxHeight: "500px",
              overflowY: "auto",
            }}
          >
            {filteredStocks.map((stock) => (
              <StockCard
                key={stock.portfolioStockId}
                stock={stock}
                showCategory={true}
                isMobile={true}
              />
            ))}
            {filteredStocks.length === 0 && (
              <div
                style={{
                  textAlign: "center",
                  color: "#9ca3af",
                  padding: "40px 20px",
                  fontSize: "14px",
                }}
              >
                No stocks found
              </div>
            )}
          </div>
        </div>

        <Stack
          justifyContent="flex-end"
          mt={3}
          sx={{
            justifyContent: "flex-end",
            width: "100%",
            flexDirection: "row",
          }}
        >
          <JarvisButton
            disableEndIcon
            sx={{ width: "155px" }}
            onClick={() => showModalHandler()}
          >
            Continue
          </JarvisButton>
        </Stack>

        <Modal
          open={showConfirmationModal}
          onClose={handleReviewAgain}
          title="Confirmation"
          size="sm"
        >
          <div style={{ textAlign: "center", padding: "20px" }}>
            <Text
              variant="h6"
              component="p"
              sx={{
                marginBottom: 3,
                fontSize: "18px",
                color: "#374151",
              }}
            >
              You've sorted your holdings as
              <br />
              <strong>
                Long Term: {longTermStocks.length} / Short Term:{" "}
                {shortTermStocks.length}
              </strong>
            </Text>

            <Text
              variant="body1"
              sx={{
                marginBottom: 4,
                color: "#6b7280",
                fontSize: "14px",
              }}
            >
              Please confirm your selection to proceed?
            </Text>

            <Stack
              direction="row"
              spacing={2}
              sx={{ justifyContent: "center" }}
            >
              <JarvisButton
                disableEndIcon
                variant="outlined"
                onClick={handleReviewAgain}
                sx={{
                  minWidth: "120px",
                  borderColor: "#d1d5db",
                  color: "#374151",
                  "&:hover": {
                    borderColor: "#9ca3af",
                    backgroundColor: "rgba(0,0,0,0.04)",
                  },
                }}
              >
                Review Again
              </JarvisButton>

              <JarvisButton
                disableEndIcon
                onClick={handleConfirm}
                sx={{
                  minWidth: "120px",
                  backgroundColor: "#10b981",
                  "&:hover": {
                    backgroundColor: "#059669",
                  },
                }}
              >
                Confirm
              </JarvisButton>
            </Stack>
          </div>
        </Modal>
      </>
    );
  }

  // Desktop layout (original code)
  return (
    <>
      <SectionTitle
        sx={{ mb: 3 }}
        heading="Set Your Stock Holding Preferences"
        subheading="Choose whether you plan to hold each stock for the long term (for steady growth) or the short term (for quick gains). You can apply your preference to all stocks or set it individually."
      />

      {/* Top Control Row */}
      <div
        style={{
          display: "flex",
          justifyContent: "space-between",
          alignItems: "center",
          marginBottom: "24px",
          padding: "16px",
          backgroundColor: "#f8fafc",
          borderRadius: "8px",
          border: "1px solid #e2e8f0",
        }}
      >
        {/* Quick Apply to All Section */}
        <div style={{ display: "flex", alignItems: "center", gap: "16px" }}>
          <span
            style={{
              fontWeight: "600",
              color: "#374151",
              fontSize: "14px",
            }}
          >
            Quick Apply to All:
          </span>
          <div style={{ display: "flex", gap: "12px" }}>
            <button
              onClick={() => applyToAll("longTerm")}
              style={{
                padding: "8px 16px",
                color: "#2B70C5",
                border: "2px solid #EEF4FA",
                borderRadius: "6px",
                fontSize: "14px",
                cursor: "pointer",
                fontWeight: "500",
                minWidth: "120px",
              }}
            >
              Long Term
            </button>
            <button
              onClick={() => applyToAll("shortTerm")}
              style={{
                padding: "8px 16px",
                color: "#2B70C5",
                border: "2px solid #EEF4FA",
                borderRadius: "6px",
                fontSize: "14px",
                cursor: "pointer",
                fontWeight: "500",
                minWidth: "120px",
              }}
            >
              Short Term
            </button>
            <button
              onClick={resetAll}
              style={{
                padding: "8px 16px",
                backgroundColor: "#ef4444",
                color: "white",
                border: "none",
                borderRadius: "6px",
                fontSize: "14px",
                cursor: "pointer",
                fontWeight: "500",
                minWidth: "120px",
              }}
            >
              Reset All
            </button>
          </div>
        </div>

        {/* Spacer */}
        <div style={{ flex: 1 }}></div>

        {/* Search Stock Section */}
        <div style={{ position: "relative", width: "300px" }}>
          <input
            type="text"
            placeholder="Search stocks by name or symbol..."
            value={searchTerm}
            onChange={(e) => setSearchTerm(e.target.value)}
            style={{
              width: "100%",
              padding: "10px 16px 10px 40px",
              border: "1px solid #d1d5db",
              borderRadius: "6px",
              fontSize: "14px",
              outline: "none",
              transition: "border-color 0.2s ease",
            }}
            onFocus={(e) => (e.target.style.borderColor = "#3b82f6")}
            onBlur={(e) => (e.target.style.borderColor = "#d1d5db")}
          />
          <svg
            style={{
              position: "absolute",
              left: "12px",
              top: "50%",
              transform: "translateY(-50%)",
              width: "16px",
              height: "16px",
              color: "#9ca3af",
            }}
            fill="none"
            stroke="currentColor"
            viewBox="0 0 24 24"
          >
            <path
              strokeLinecap="round"
              strokeLinejoin="round"
              strokeWidth={2}
              d="M21 21l-6-6m2-5a7 7 0 11-14 0 7 7 0 0114 0z"
            />
          </svg>
        </div>
      </div>

      {/* Main Content Row */}
      <div
        style={{
          display: "grid",
          gridTemplateColumns: "1fr 1fr 1fr",
          gap: "20px",
          minHeight: "500px",
        }}
      >
        {/* Available Stocks Section */}
        <div
          onDragOver={(e) => handleDragOver(e, "unassigned")}
          onDrop={(e) => handleDrop(e, "unassigned")}
          style={{
            padding: "20px",
            border: "2px dashed #d1d5db",
            borderRadius: "12px",
            backgroundColor: "#fafafa",
          }}
        >
          <div
            style={{
              display: "flex",
              justifyContent: "space-between",
              alignItems: "center",
              marginBottom: "16px",
            }}
          >
            <h3
              style={{
                margin: 0,
                color: "#374151",
                fontSize: "16px",
                fontWeight: "600",
              }}
            >
              Stocks{" "}
              <span style={{ fontSize: "14px", color: "#6b7280" }}>
                ({unassignedStocks.length})
              </span>
            </h3>
          </div>
          <div
            style={{
              minHeight: "400px",
              maxHeight: "400px",
              overflowY: "auto",
              padding: "4px",
            }}
          >
            {filteredStocks
              .filter(
                (stock) => !stock.category || stock.category === "unassigned"
              )
              .map((stock) => (
                <StockCard
                  key={stock.portfolioStockId}
                  stock={stock}
                  showCategory={true}
                />
              ))}
            {filteredStocks.filter(
              (stock) => !stock.category || stock.category === "unassigned"
            ).length === 0 && (
              <div
                style={{
                  textAlign: "center",
                  color: "#9ca3af",
                  padding: "40px 20px",
                  fontSize: "14px",
                }}
              >
                No stocks available
              </div>
            )}
          </div>
        </div>

        {/* Long Term Holding Preference */}
        <div
          onDragOver={(e) => handleDragOver(e, "longTerm")}
          onDrop={(e) => handleDrop(e, "longTerm")}
          style={{
            padding: "20px",
            border: "2px dashed #10b981",
            borderRadius: "12px",
            backgroundColor: "#f0fdf4",
          }}
        >
          <h3
            style={{
              margin: "0 0 16px 0",
              color: "#10b981",
              fontSize: "16px",
              fontWeight: "600",
            }}
          >
            Long Term Holding Preference{" "}
            <span style={{ fontSize: "14px", color: "#6b7280" }}>
              ({longTermStocks.length})
            </span>
          </h3>
          <div
            style={{
              minHeight: "400px",
              maxHeight: "400px",
              overflowY: "auto",
              padding: "4px",
            }}
          >
            {longTermStocks.map((stock) => (
              <StockCard key={stock.portfolioStockId} stock={stock} />
            ))}
            {longTermStocks.length === 0 && (
              <div
                style={{
                  textAlign: "center",
                  color: "#9ca3af",
                  padding: "40px 20px",
                  fontSize: "14px",
                }}
              >
                Drag stocks here for long term
              </div>
            )}
          </div>
        </div>

        {/* Short Term Holding Preference */}
        <div
          onDragOver={(e) => handleDragOver(e, "shortTerm")}
          onDrop={(e) => handleDrop(e, "shortTerm")}
          style={{
            padding: "20px",
            border: "2px dashed #10b981",
            borderRadius: "12px",
            backgroundColor: "#f0fdf4",
          }}
        >
          <h3
            style={{
              margin: "0 0 16px 0",
              color: "#10b981",
              fontSize: "16px",
              fontWeight: "600",
            }}
          >
            Short Term Holding Preference{" "}
            <span style={{ fontSize: "14px", color: "#6b7280" }}>
              ({shortTermStocks.length})
            </span>
          </h3>
          <div
            style={{
              minHeight: "400px",
              maxHeight: "400px",
              overflowY: "auto",
              padding: "4px",
            }}
          >
            {shortTermStocks.map((stock) => (
              <StockCard key={stock.portfolioStockId} stock={stock} />
            ))}
            {shortTermStocks.length === 0 && (
              <div
                style={{
                  textAlign: "center",
                  color: "#9ca3af",
                  padding: "40px 20px",
                  fontSize: "14px",
                }}
              >
                Drag stocks here for short term
              </div>
            )}
          </div>
        </div>
      </div>

      <Stack
        justifyContent="flex-end"
        mt={3}
        sx={{
          justifyContent: "flex-end",
          width: "100%",
          flexDirection: "row",
        }}
      >
        <JarvisButton
          disableEndIcon
          sx={{ width: "155px" }}
          onClick={() => showModalHandler()}
        >
          Continue
        </JarvisButton>
      </Stack>

      <Modal
        open={showConfirmationModal}
        onClose={handleReviewAgain}
        title="Confirmation"
        size="sm"
      >
        <div style={{ textAlign: "center", padding: "20px" }}>
          <Text
            variant="h6"
            component="p"
            sx={{
              marginBottom: 3,
              fontSize: "18px",
              color: "#374151",
            }}
          >
            You've sorted your holdings as
            <br />
            <strong>Long Term / Short Term</strong>
          </Text>

          <Text
            variant="body1"
            sx={{
              marginBottom: 4,
              color: "#6b7280",
              fontSize: "14px",
            }}
          >
            Please confirm your selection to proceed?
          </Text>

          <Stack direction="row" spacing={2} sx={{ justifyContent: "center" }}>
            <JarvisButton
              disableEndIcon
              variant="outlined"
              onClick={handleReviewAgain}
              sx={{
                minWidth: "120px",
                borderColor: "#d1d5db",
                color: "#374151",
                "&:hover": {
                  borderColor: "#9ca3af",
                  backgroundColor: "rgba(0,0,0,0.04)",
                },
              }}
            >
              Review Again
            </JarvisButton>

            <JarvisButton
              disableEndIcon
              onClick={handleConfirm}
              sx={{
                minWidth: "120px",
                backgroundColor: "#10b981",
                "&:hover": {
                  backgroundColor: "#059669",
                },
              }}
            >
              Confirm
            </JarvisButton>
          </Stack>
        </div>
      </Modal>

      <style jsx>{`
        .stock-card:hover {
          transform: translateY(-2px);
          box-shadow: 0 4px 8px rgba(0, 0, 0, 0.1);
          background-color: #fff;
        }

        .stock-card:active {
          cursor: grabbing;
        }

        .stock-card:focus {
          outline: 2px solid #3b82f6;
        }

        input:focus {
          border-color: #3b82f6 !important;
          box-shadow: 0 0 0 3px rgba(59, 130, 246, 0.1);
        }
      `}</style>
    </>
  );
}

export default RMSDragNDrop;
